// MonthlySubscriptionDialog.jsx
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Box,
  TextField,
  IconButton,
  CircularProgress,
  InputAdornment,
  Paper
} from "@mui/material";
import Card from "@mui/material/Card";
import Icon from "../../../components/icon";
import { useEffect, useState } from "react";
import Stripecheckout from "images/Stripecheckout.jpg";
import { GetSubscriptionDetails } from "components/logic/paymentgateway/getSubscriptionDetails";
import { formatUSCurrency } from "components/CurrencyFormat"; 
import { GetSession } from "components/logic/paymentgateway/getSession";
import { loadStripe } from "@stripe/stripe-js";
import CustomTextField from "components/mui/text-field";
import { GetCompanyPromoCode, ValidatePromoCode } from "../logic/SubscriptionHandler";
import BillingSummarySkeleton from "./SummarySkeleonLoader";

const stripePromise = loadStripe(localStorage.getItem('publicKey'));

const MonthlySubscriptionDialog = ({ open, onClose, navigate }) => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [calculation, setCalculation] = useState({});
  const [sessionId, setSessionId] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [isApplied, setIsApplied] = useState(false);
  const [grandTotal, setGrandTotal] = useState(0);
  const [companyPromo, setCompanyPromo] = useState('');
  const [discount, setDiscount] = useState(0);
  const [isCalculationLoading, setIsCalculationLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handlePayNow =  async () => {
    if(sessionId === '') {
      console.log("Session ID is not available.");
      return;
    }        
      try {
        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          console.log(`Error: ${error.message}`);
        }
      } catch (err) {
        console.log(`Failed to redirect: ${err.message}`);
      }finally {
        setIsProcessingPayment(false); 
      }
  };

  const getSubscriptionDetails = async () => {
    setIsCalculationLoading(true);
    const response = await GetSubscriptionDetails(navigate);
    if (response && !response.isError) {
      setCalculation(response.result);
    }
  }

  const getCompanyPromo = async () => {
    try{
      const response = await GetCompanyPromoCode(navigate);
      if(response.hasPromo){
        setCompanyPromo(response.promoCode);
      }      
    }catch(err){
      console.log("Error fetching promo code: ", err);      
    }finally{
      setIsCalculationLoading(false);
    }
  }

  const validatePromo = async (promo) => {
    try{
      const response = await ValidatePromoCode(promo,navigate);      
      return response;        
    }catch(err){
      console.log("Error fetching promo code: ", err);
      return null;
    }
  }

  const getSession = async () => {
    setIsProcessingPayment(true);
    const response = await GetSession(navigate, promoCode);
    if (response && !response.isError) {
      setSessionId(response.result.sessionId);
      localStorage.setItem('publicKey', response.result.publicKey);    
    }
  }

  useEffect(() => {
    if(open && sessionId) {
      handlePayNow();
    }
  },[sessionId]);

  useEffect(() => {
    let Mounted = true;
    if (open && Mounted) {            
    getCompanyPromo();
    getSubscriptionDetails();  
    }
    return () => {
      Mounted = false;
    }    
  }, [open]);

  useEffect(() => {
    let Mounted = true;
    if (open && Mounted) {
      setIsCalculationLoading(true);
      if(companyPromo){
        setPromoCode(companyPromo);
        ApplyPromoCode(companyPromo);
      }else{
        setGrandTotal(calculation.amount);
      }
      setIsCalculationLoading(false);
    }
    return () => {
      Mounted = false;
    }    
  },[companyPromo,calculation]);

    useEffect(() => {
      if (Object.keys(errors).length > 0) {
        const timer = setTimeout(() => {
          setErrors({});
        }, 5000);
        return () => clearTimeout(timer);
      }
    }, [errors]);

  const handleSuccessClose = () => {
    setPaymentSuccess(false);
    handleClose();
  };

  const handleClose = () => {
    setErrors({});
    handleDeletePromo();
    onClose();
  }


  const ApplyPromoCode = async (promo) => {
    if(!promo) {       
      return;
    }
    const result = await validatePromo(promo);
    if(result.isValid){
      setIsApplied(true);
      if(result.percentageOff){
        const discountAmount = (calculation.amount * result.percentageOff ) / 100;
        setDiscount(discountAmount);
        const newTotal = calculation.amount - discountAmount;
        setGrandTotal(newTotal);
        //setCalculation(prev => ({...prev, amount: newTotal}));        
      }else{
        const discountAmount = result.amountOff/100;
        setDiscount(discountAmount);
        const newTotal = calculation.amount - (discountAmount);
        setGrandTotal(newTotal);
        //setCalculation(prev => ({...prev, amount: newTotal}));
      }  
    }else{
      setErrors({ ...errors, invalidPromo: result?.message || "Invalid Promo Code" });
    }
  }

  const formatNumber = (val) => {
    const cleaned = val.replace(/[^0-9.]/g, "");
    const [intPart, decimalPart] = cleaned.split(".");
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimalPart !== undefined ? `${formattedInt}.${decimalPart}` : formattedInt;
  };

  const handleCustomAmountChange = (e) => {
    const input = e.target.value;
    const formatted = formatNumber(input);
    setCustomAmount(formatted);
  };

  const handleDeletePromo = () => {
    setIsApplied(false);
    setPromoCode('');
    setDiscount(0);
    setGrandTotal(calculation.amount);
  }

  return (
    <>
      {/* Main Subscription Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 4, pb: 2, position: 'relative', ml: 3 }}>
          <Typography variant="h4">Setup Monthly Subscription</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Choose your plan and continue with unlimited access to all features.
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 26,
              top: 28,
              color: 'text.secondary',
            }}
          >
            <Icon icon="tabler:x" />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ my: 2 }} />
        <DialogContent sx={{ p: 4, pt: 0 }}>
          <Card
            sx={{
              p: 5,
              height: "100%",
              border: "1px solid",
              borderColor: "divider",
              width: "100%",
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  display: "inline-block",
                  bgcolor: "#F5F5F5",
                  color: "#23A26D",
                  borderRadius: "16px",
                  px: 2.5,
                  py: 0.5,
                  mb: 1,
                  fontSize: '0.75rem',
                  lineHeight: 1.5
                }}
                gutterBottom
              >
                Base Price
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: 600, color: "#23A26D" }}
                gutterBottom
              >
                {formatUSCurrency(calculation?.baseAmount)+" "}
                <Typography
                  component="span"
                  variant="body1"
                  color="#23A26D"
                  sx={{ opacity: 0.6 }}
                >
                  /month
                </Typography>
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 550 }}
                paragraph
              >
                Includes {calculation?.numberOfUsers || "one"} User
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 550 }}
                paragraph
              >
                Price : {formatUSCurrency(calculation?.effectivePricePerUser) || "20"}/User
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 550 }}
                paragraph
              >
                User amount : {formatUSCurrency(calculation?.userAmount) || "20"}
              </Typography>
              <Typography variant="body2" paragraph>
                Gives us a fighting chance to provide solid support and a perfect product. We don't paywall any features; enjoy full access.
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  mt: 4,
                }}
              >
                <Icon
                  icon="tabler:progress-check"
                  width={18}
                  height={18}
                  style={{ marginRight: 4, color: "#23A26D" }}
                />
                All Features Included
              </Typography>
            </Box>
          </Card>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 3, mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 3, mt: 4 }}>
              <CustomTextField
                label="Enter Promo Code"
                variant="outlined"                
                value={promoCode || ""}
                disabled={isApplied}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Enter Promo Code"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    '& fieldset': {
                      borderColor: 'divider',
                    },
                  },
                }}
                error={!!errors.invalidPromo}
                helperText={errors.invalidPromo}
              />
            </Box>
            {isApplied &&
              <IconButton
                color="error"
                onClick={handleDeletePromo}
                sx={{ boxShadow: 'none', mt: 6 }}
              >
                <Icon icon='tabler:trash' />
              </IconButton>
            }
            <Button
              variant="contained"
              color="info"
              sx={{ boxShadow: 'none', mt:6, display: isApplied?'none':'block' }}
              onClick={()=>{ApplyPromoCode(promoCode)}}
              
            >
              Apply
            </Button>            
          </Box>
          
          {/* Billing Summary Card */}
          {isCalculationLoading ? (
            <BillingSummarySkeleton/>
          ):(
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  backgroundColor: 'background.paper'
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  Order Summary
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {/* Subtotal */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                      Subtotal
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                      {formatUSCurrency(calculation.amount) || "0"}
                    </Typography>
                  </Box>

                  {/* Discount */}
                  {discount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" sx={{ color: 'success.main', fontSize: '0.95rem' }}>
                        Discount
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 500, fontSize: '0.95rem' }}>
                        -{formatUSCurrency(discount) || "0"}
                      </Typography>
                    </Box>
                  )}

                  {/* Divider */}
                  <Divider sx={{ my: 1 }} />

                  {/* Grand Total */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1rem', color: 'text.primary' }}>
                      Total Amount
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.25rem' }}>
                      {formatUSCurrency(grandTotal) || "0"}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
          )}          
        </DialogContent>
        <Divider sx={{ my: 1 }} />
        <DialogActions sx={{ p: 4, mb: 2 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="main"
            sx={{ boxShadow: 'none' }}
            onClick={getSession}
          >
            Pay Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stripe Checkout Redirect Dialog */}
      <Dialog open={isProcessingPayment} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Redirect to Stripe Checkout
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img src={Stripecheckout} alt="Stripe Checkout" style={{ maxWidth: '100%', height: 'auto' }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body1" color="text.secondary">
              Redirecting to secure payment...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Payment Success Dialog */}
      <Dialog open={paymentSuccess} maxWidth="sm">
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
              bgcolor: '#F0F9F5',
              width: 50,
              height: 50,
              borderRadius: '50%',
              alignItems: 'center',
              mx: 'auto',
            }}
          >
            <Icon icon="eva:checkmark-circle-outline" width={30} height={30} color="#23A26D" />
          </Box>
          <Typography variant="h5" gutterBottom>
            Payment successful!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your payment has been successfully processed.
          </Typography>
          <Button
            variant="contained"
            color="main"
            sx={{ boxShadow: 'none', minWidth: 200 }}
            onClick={handleSuccessClose}
          >
            Go to Dashboard
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MonthlySubscriptionDialog;